/**
 * RWA Service - Uses stratos-wallet-sdk for Canton contract operations
 *
 * This service provides RWA-specific methods that wrap the generic SDK
 * Canton operations (cantonQuery, cantonCreate, cantonExercise).
 */

import { getSDK, type StratosSDK } from '@stratos-wallet/sdk';

// ============================================
// Types matching Daml contracts
// ============================================

export type AssetCategory =
  | 'RealEstate'
  | 'Commodities'
  | 'Art'
  | 'Collectibles'
  | 'Securities'
  | 'Infrastructure'
  | 'IntellectualProperty';

export type AssetType = 'Fungible' | 'NonFungible';

export type InvestorStatus =
  | 'Unverified'
  | 'KYCVerified'
  | 'AccreditedVerified'
  | 'InstitutionalVerified';

export interface ComplianceConfig {
  requireAccreditedInvestor: boolean;
  requireKYC: boolean;
  maxHolders: number | null;
  maxHoldingPercentage: number | null;
  minInvestmentAmount: number | null;
  transferRestrictionDays: number | null;
  jurisdictionWhitelist: string[];
  jurisdictionBlacklist: string[];
}

export interface UnderlyingAsset {
  description: string;
  legalEntity: string | null;
  custodian: string | null;
  valuationDate: string | null;
  valuationAmount: string | null;
  valuationCurrency: string;
  documents: string[];
  physicalLocation: string | null;
}

export interface PaymentToken {
  symbol: string;
  issuer: string;
  decimals: string;
}

// ============================================
// Role Types
// ============================================

export type UserRole = 'operator' | 'issuer' | 'verifier' | 'investor';

export interface UserRoles {
  roles: Set<UserRole>;
  isOperator: boolean;
  isIssuer: boolean;
  isVerifier: boolean;
  isInvestor: boolean;
  issuerLicense: {
    contractId: string;
    allowedCategories: string[];
  } | null;
  verifierContract: {
    contractId: string;
  } | null;
}

// ============================================
// Contract Payloads
// ============================================

export interface IssuerLicensePayload {
  operator: string;
  issuer: string;
  issuerName: string;
  allowedCategories: { tag: AssetCategory }[];
  appointedAt: string;
}

export interface VerifierPayload {
  operator: string;
  verifier: string;
  verifierName: string;
  appointedAt: string;
}

export interface IssuerProposalPayload {
  operator: string;
  issuer: string;
  issuerName: string;
  allowedCategories: { tag: AssetCategory }[];
}

export interface AssetProposalPayload {
  operator: string;
  issuer: string;
  assetId: string;
  name: string;
  symbol: string;
  category: { tag: AssetCategory };
  assetType: { tag: AssetType };
  totalSupply: string;
  compliance: ComplianceConfig;
  underlying: UnderlyingAsset;
}

export interface PlatformPayload {
  operator: string;
  config: {
    platformName: string;
    platformFeePercent: string;
    feeCollector: string;
    allowedPaymentTokens: PaymentToken[];
    paused: boolean;
  };
  issuers: string[];
  verifiers: string[];
  investors: string[];
}

export interface RWAAssetPayload {
  operator: string;
  issuer: string;
  assetId: string;
  name: string;
  symbol: string;
  description: string;
  category: { tag: AssetCategory };
  assetType: { tag: AssetType };
  totalSupply: string;
  circulatingSupply: string;
  compliance: ComplianceConfig;
  underlying: UnderlyingAsset;
  imageUrl: { tag: 'Some'; value: string } | { tag: 'None' } | null;
  metadata: Array<{ _1: string; _2: string }>;
  isActive: boolean;
  createdAt: string;
}

export interface RWAHoldingPayload {
  operator: string;
  issuer: string;
  owner: string;
  assetId: string;
  amount: string;
  lockedAmount: string;
  acquiredAt: string;
}

export interface PaymentHoldingPayload {
  operator: string;
  owner: string;
  token: PaymentToken;
  amount: string;
  lockedAmount: string;
}

export interface RWAListingPayload {
  operator: string;
  public: string;
  seller: string;
  assetId: string;
  assetSymbol: string;
  assetName: string;
  amount: string;
  pricePerToken: string;
  paymentToken: PaymentToken;
  description: string;
  imageUrl: { tag: 'Some'; value: string } | { tag: 'None' } | null;
  isActive: boolean;
  createdAt: string;
  sellerHoldingCid: string;
}

export interface ListingProposalPayload {
  operator: string;
  public: string;
  seller: string;
  assetId: string;
  assetSymbol: string;
  assetName: string;
  amount: string;
  pricePerToken: string;
  paymentToken: PaymentToken;
  description: string;
  imageUrl: { tag: 'Some'; value: string } | { tag: 'None' } | null;
  sellerHoldingCid: string;
}

export interface PaymentDepositRequestPayload {
  operator: string;
  owner: string;
  token: PaymentToken;
  amount: string;
}

export interface RWATradePayload {
  operator: string;
  seller: string;
  buyer: string;
  assetId: string;
  amount: string;
  pricePerToken: string;
  totalPrice: string;
  paymentToken: PaymentToken;
  sellerHoldingCid: string;
  buyerPaymentCid: string;
  status: string;
  createdAt: string;
}

export interface InvestorProfilePayload {
  operator: string;
  investor: string;
  verifier: string;
  status: { tag: InvestorStatus };
  jurisdiction: string;
  verifiedAt: string;
  expiryDate: { tag: 'Some'; value: string } | { tag: 'None' };
}

// ============================================
// Display Types (for UI)
// ============================================

export interface RWAAsset {
  contractId: string;
  assetId: string;
  name: string;
  symbol: string;
  description?: string;
  category: AssetCategory;
  assetType: AssetType;
  issuer: string;
  totalSupply: number;
  circulatingSupply: number;
  compliance: ComplianceConfig;
  underlying: UnderlyingAsset;
  imageUrl?: string;
  metadata?: Array<{ key: string; value: string }>;
  isActive: boolean;
}

export interface RWAHolding {
  contractId: string;
  assetId: string;
  owner: string;
  amount: number;
  lockedAmount: number;
}

export interface PaymentBalance {
  contractId: string;
  token: string;
  amount: number;
  lockedAmount: number;
}

export interface Listing {
  contractId: string;
  seller: string;
  assetId: string;
  assetSymbol: string;
  assetName: string;
  description: string;
  imageUrl?: string;
  amount: number;
  pricePerToken: number;
  totalPrice: number;
  paymentToken: string;
  isActive: boolean;
  sellerHoldingCid: string;
}

export interface ListingProposal {
  contractId: string;
  seller: string;
  assetId: string;
  assetSymbol: string;
  assetName: string;
  amount: number;
  pricePerToken: number;
  paymentToken: string;
  sellerHoldingCid: string;
}

export interface PaymentDepositReq {
  contractId: string;
  owner: string;
  token: string;
  amount: number;
}

export interface Trade {
  contractId: string;
  seller: string;
  buyer: string;
  assetId: string;
  amount: number;
  pricePerToken: number;
  totalPrice: number;
}

export interface InvestorProfile {
  contractId: string;
  investor: string;
  status: InvestorStatus;
  jurisdiction: string;
  verifier: string;
}

// ============================================
// RWA Service Class
// ============================================

export class RWAService {
  private sdk: StratosSDK;
  private packageId: string;
  private publicParty: string | null;
  private operatorPartyOverride: string | null;

  constructor(packageId: string, sdk?: StratosSDK, publicParty?: string, operatorParty?: string) {
    this.sdk = sdk || getSDK();
    this.packageId = packageId;
    this.publicParty = publicParty || null;
    this.operatorPartyOverride = operatorParty || null;
  }

  setPublicParty(publicParty: string) {
    this.publicParty = publicParty;
  }

  setOperatorParty(operatorParty: string) {
    this.operatorPartyOverride = operatorParty;
  }

  private templateId(module: string, template: string): string {
    return `${this.packageId}:${module}:${template}`;
  }

  /** Query contracts, filtering out results from other package versions (Canton does cross-package matching) */
  private async query<T>(params: { templateId: string; readAs?: string[]; filter?: Record<string, unknown> }): Promise<Array<{ contractId: string; templateId: string; payload: T }>> {
    const contracts = await this.sdk.cantonQuery<T>(params);
    if (contracts.length > 0) {
      console.log('[RWA] query filter:', { requested: params.templateId, packageId: this.packageId, total: contracts.length, sampleTemplateId: (contracts[0] as any).templateId, keys: Object.keys(contracts[0]) });
    }
    const filtered = contracts.filter(c => {
      const tid = (c as any).templateId;
      return tid && tid.startsWith(this.packageId + ':');
    });
    if (contracts.length !== filtered.length) {
      console.log('[RWA] query filtered:', { before: contracts.length, after: filtered.length });
    }
    return filtered;
  }

  // ============================================
  // Asset Queries
  // ============================================

  async getAssets(): Promise<RWAAsset[]> {
    const contracts = await this.query<RWAAssetPayload>({
      templateId: this.templateId('RWAAsset', 'RWAAsset'),
      readAs: this.publicParty ? [this.publicParty] : undefined,
    });

    return contracts.map(c => {
      const imageUrlRaw = c.payload.imageUrl;
      const imageUrl = imageUrlRaw && typeof imageUrlRaw === 'object' && 'tag' in imageUrlRaw && imageUrlRaw.tag === 'Some'
        ? imageUrlRaw.value
        : (typeof imageUrlRaw === 'string' ? imageUrlRaw : undefined);

      const metadataRaw = c.payload.metadata;
      const metadata = Array.isArray(metadataRaw)
        ? metadataRaw.map(m => ({ key: m._1, value: m._2 }))
        : undefined;

      return {
        contractId: c.contractId,
        assetId: c.payload.assetId,
        name: c.payload.name,
        symbol: c.payload.symbol,
        description: c.payload.description || undefined,
        category: c.payload.category.tag,
        assetType: c.payload.assetType.tag,
        issuer: c.payload.issuer,
        totalSupply: parseFloat(c.payload.totalSupply),
        circulatingSupply: parseFloat(c.payload.circulatingSupply),
        compliance: c.payload.compliance,
        underlying: c.payload.underlying,
        imageUrl,
        metadata,
        isActive: c.payload.isActive,
      };
    });
  }

  async getAsset(assetId: string): Promise<RWAAsset | null> {
    const assets = await this.getAssets();
    return assets.find(a => a.assetId === assetId) || null;
  }

  // ============================================
  // Holdings
  // ============================================

  async getHoldings(owner?: string): Promise<RWAHolding[]> {
    const contracts = await this.query<RWAHoldingPayload>({
      templateId: this.templateId('RWAAsset', 'RWAHolding'),
    });

    const holdings = contracts.map(c => ({
      contractId: c.contractId,
      assetId: c.payload.assetId,
      owner: c.payload.owner,
      amount: parseFloat(c.payload.amount),
      lockedAmount: parseFloat(c.payload.lockedAmount),
    })).filter(h => (h.amount + h.lockedAmount) > 0.0001);

    if (owner) {
      return holdings.filter(h => h.owner === owner);
    }
    return holdings;
  }

  async getMyHoldings(): Promise<RWAHolding[]> {
    const partyId = await this.sdk.getPartyId();
    if (!partyId) return [];
    return this.getHoldings(partyId);
  }

  // ============================================
  // Payment Holdings
  // ============================================

  async getPaymentBalances(owner?: string): Promise<PaymentBalance[]> {
    const contracts = await this.query<PaymentHoldingPayload>({
      templateId: this.templateId('RWAMarketplace', 'PaymentHolding'),
    });

    const balances = contracts.map(c => ({
      contractId: c.contractId,
      token: c.payload.token.symbol,
      amount: parseFloat(c.payload.amount),
      lockedAmount: parseFloat(c.payload.lockedAmount),
    }));

    if (owner) {
      return balances.filter(b =>
        contracts.find(c => c.contractId === b.contractId)?.payload.owner === owner
      );
    }
    return balances;
  }

  async getMyPaymentBalance(tokenSymbol: string = 'USDC'): Promise<number> {
    const partyId = await this.sdk.getPartyId();
    if (!partyId) return 0;

    const balances = await this.getPaymentBalances(partyId);
    // Sum all PaymentHoldings for this token (each ApproveDeposit creates a separate contract)
    const total = balances
      .filter(b => b.token === tokenSymbol)
      .reduce((sum, b) => sum + b.amount, 0);
    return total;
  }

  // ============================================
  // Listings
  // ============================================

  async getListings(): Promise<Listing[]> {
    const contracts = await this.query<RWAListingPayload>({
      templateId: this.templateId('RWAMarketplace', 'RWAListing'),
      readAs: this.publicParty ? [this.publicParty] : undefined,
    });

    return contracts
      .filter(c => c.payload.isActive)
      .map(c => {
        const imageUrlRaw = c.payload.imageUrl;
        const imageUrl = imageUrlRaw && typeof imageUrlRaw === 'object' && 'tag' in imageUrlRaw && imageUrlRaw.tag === 'Some'
          ? imageUrlRaw.value
          : undefined;

        return {
          contractId: c.contractId,
          seller: c.payload.seller,
          assetId: c.payload.assetId,
          assetSymbol: c.payload.assetSymbol,
          assetName: c.payload.assetName,
          description: c.payload.description,
          imageUrl,
          amount: parseFloat(c.payload.amount),
          pricePerToken: parseFloat(c.payload.pricePerToken),
          totalPrice: parseFloat(c.payload.amount) * parseFloat(c.payload.pricePerToken),
          paymentToken: c.payload.paymentToken.symbol,
          isActive: c.payload.isActive,
          sellerHoldingCid: c.payload.sellerHoldingCid,
        };
      });
  }

  async getListingsForAsset(assetId: string): Promise<Listing[]> {
    const listings = await this.getListings();
    return listings.filter(l => l.assetId === assetId);
  }

  // ============================================
  // Trades
  // ============================================

  async getPendingTrades(): Promise<Trade[]> {
    const contracts = await this.query<RWATradePayload>({
      templateId: this.templateId('RWAMarketplace', 'RWATrade'),
    });
    console.log('[RWA] getPendingTrades:', contracts.length, 'trade contracts found');
    if (contracts.length > 0) {
      contracts.forEach(c => console.log('[RWA] trade:', c.contractId, c.payload.seller, c.payload.buyer, c.payload.assetId, c.payload.status));
    }

    return contracts.map(c => ({
      contractId: c.contractId,
      seller: c.payload.seller,
      buyer: c.payload.buyer,
      assetId: c.payload.assetId,
      amount: parseFloat(c.payload.amount),
      pricePerToken: parseFloat(c.payload.pricePerToken),
      totalPrice: parseFloat(c.payload.totalPrice),
    }));
  }

  async getMyTrades(): Promise<Trade[]> {
    const partyId = await this.sdk.getPartyId();
    if (!partyId) return [];

    const trades = await this.getPendingTrades();
    return trades.filter(t => t.seller === partyId || t.buyer === partyId);
  }

  // ============================================
  // Investor Profile
  // ============================================

  async getMyProfile(): Promise<InvestorProfile | null> {
    const partyId = await this.sdk.getPartyId();
    if (!partyId) return null;

    const contracts = await this.query<InvestorProfilePayload>({
      templateId: this.templateId('RWAOperator', 'RWAInvestorProfile'),
    });

    const profile = contracts.find(c => c.payload.investor === partyId);
    if (!profile) return null;

    return {
      contractId: profile.contractId,
      investor: profile.payload.investor,
      status: profile.payload.status.tag,
      jurisdiction: profile.payload.jurisdiction,
      verifier: profile.payload.verifier,
    };
  }

  // ============================================
  // Actions - Transfer
  // ============================================

  async transferTokens(
    holdingContractId: string,
    recipient: string,
    amount: number
  ): Promise<string> {
    const result = await this.sdk.cantonExercise({
      contractId: holdingContractId,
      templateId: this.templateId('RWAAsset', 'RWAHolding'),
      choice: 'Transfer',
      argument: {
        recipient: recipient,
        transferAmount: amount.toString(),
      },
      actAs: [recipient],  // recipient needed as signatory for new RWAHolding creation
    });

    // Return new holding contract ID
    const event = result.events?.find(e => e.templateId?.includes('RWAHolding'));
    return event?.contractId || '';
  }

  // ============================================
  // Actions - Create Listing Proposal (2-step: lock + propose)
  // ============================================

  async createListingProposal(
    holdingContractId: string,
    asset: { assetId: string; assetSymbol: string; assetName: string; description: string; imageUrl?: string },
    amount: number,
    pricePerToken: number,
    paymentToken: PaymentToken
  ): Promise<string> {
    const partyId = await this.sdk.getPartyId();
    if (!partyId) throw new Error('Not connected');
    if (!this.publicParty) throw new Error('Public party not set');

    // Check for existing pending proposals for this asset
    const existingProposals = await this.getListingProposals().catch(() => []);
    if (existingProposals.some(p => p.assetId === asset.assetId && p.seller === partyId)) {
      throw new Error('You already have a pending listing proposal for this asset');
    }

    // Check for existing active listings for this asset
    const existingListings = await this.getListings().catch(() => []);
    if (existingListings.some(l => l.assetId === asset.assetId && l.seller === partyId)) {
      throw new Error('This asset is already listed on the marketplace');
    }

    // Step 1: Lock tokens on the holding (check if already locked)
    let newHoldingCid = holdingContractId;
    const holdings = await this.getMyHoldings();
    const currentHolding = holdings.find(h => h.contractId === holdingContractId);

    if (currentHolding && currentHolding.amount >= amount) {
      // Tokens available — lock them
      const lockResult = await this.sdk.cantonExercise({
        contractId: holdingContractId,
        templateId: this.templateId('RWAAsset', 'RWAHolding'),
        choice: 'LockTokens',
        argument: {
          lockAmount: amount.toString(),
        },
      });

      // Try to get new CID from exercise response events first
      const holdingEvent = lockResult.events?.find((e: any) =>
        e.templateId && e.templateId.includes('RWAHolding')
      );
      if (holdingEvent?.contractId) {
        newHoldingCid = holdingEvent.contractId;
        console.log('[RWA] LockTokens: got new CID from events:', newHoldingCid);
      } else {
        // Fallback: re-query holdings, but EXCLUDE the old archived CID
        console.log('[RWA] LockTokens: no events, re-querying. Events:', JSON.stringify(lockResult.events));
        const freshHoldings = await this.getHoldings(currentHolding.owner);
        const lockedHolding = freshHoldings.find(h =>
          h.assetId === currentHolding.assetId &&
          h.lockedAmount >= amount &&
          h.contractId !== holdingContractId
        );
        if (!lockedHolding) throw new Error('Failed to find locked holding after LockTokens. Fresh holdings: ' + JSON.stringify(freshHoldings.map(h => ({ cid: h.contractId, asset: h.assetId, amt: h.amount, locked: h.lockedAmount }))));
        newHoldingCid = lockedHolding.contractId;
        console.log('[RWA] LockTokens: got new CID from re-query:', newHoldingCid);
      }
      console.log('[RWA] LockTokens: old CID', holdingContractId, '→ new CID', newHoldingCid);
    } else if (currentHolding && currentHolding.lockedAmount >= amount) {
      // Tokens already locked (from a previous delist that didn't unlock) — proceed with current holding
      console.log('[RWA] Tokens already locked, skipping LockTokens');
    } else {
      throw new Error('No available tokens to list. Available: ' + (currentHolding?.amount || 0) + ', Locked: ' + (currentHolding?.lockedAmount || 0));
    }

    // Step 2: Create ListingProposal
    const operatorParty = await this.getOperator();
    if (!operatorParty) throw new Error('Operator not found');

    const result = await this.sdk.cantonCreate({
      templateId: this.templateId('RWAMarketplace', 'ListingProposal'),
      payload: {
        operator: operatorParty,
        public: this.publicParty,
        seller: partyId,
        assetId: asset.assetId,
        assetSymbol: asset.assetSymbol,
        assetName: asset.assetName,
        amount: amount.toString(),
        pricePerToken: pricePerToken.toString(),
        paymentToken,
        description: asset.description,
        imageUrl: asset.imageUrl || null,
        sellerHoldingCid: newHoldingCid,
      },
    });

    return result.contractId;
  }

  // ============================================
  // Actions - Listing Proposal Management
  // ============================================

  async getListingProposals(): Promise<ListingProposal[]> {
    const contracts = await this.query<ListingProposalPayload>({
      templateId: this.templateId('RWAMarketplace', 'ListingProposal'),
    });

    return contracts.map(c => ({
      contractId: c.contractId,
      seller: c.payload.seller,
      assetId: c.payload.assetId,
      assetSymbol: c.payload.assetSymbol,
      assetName: c.payload.assetName,
      amount: parseFloat(c.payload.amount),
      pricePerToken: parseFloat(c.payload.pricePerToken),
      paymentToken: c.payload.paymentToken.symbol,
      sellerHoldingCid: c.payload.sellerHoldingCid,
    }));
  }

  async approveListingProposal(contractId: string): Promise<string> {
    const result = await this.sdk.cantonExercise({
      contractId,
      templateId: this.templateId('RWAMarketplace', 'ListingProposal'),
      choice: 'ApproveListing',
      argument: {
        approvalTime: new Date().toISOString(),
      },
    });

    const event = result.events?.find((e: any) => e.templateId?.includes('RWAListing'));
    return event?.contractId || '';
  }

  async rejectListingProposal(contractId: string, reason: string): Promise<void> {
    await this.sdk.cantonExercise({
      contractId,
      templateId: this.templateId('RWAMarketplace', 'ListingProposal'),
      choice: 'RejectListing',
      argument: { reason },
    });
  }

  // ============================================
  // Actions - Delist / Withdraw Proposal
  // ============================================

  async delistAsset(listingContractId: string, holdingContractId: string, amount: number): Promise<void> {
    // Step 1: Deactivate the listing
    await this.sdk.cantonExercise({
      contractId: listingContractId,
      templateId: this.templateId('RWAMarketplace', 'RWAListing'),
      choice: 'DeactivateListing',
      argument: {},
    });

    // Step 2: Unlock tokens using the actual holding CID (passed from UI, always fresh)
    await this.sdk.cantonExercise({
      contractId: holdingContractId,
      templateId: this.templateId('RWAAsset', 'RWAHolding'),
      choice: 'UnlockTokens',
      argument: { unlockAmount: amount.toString() },
    });
  }

  async withdrawListingProposal(proposalContractId: string, holdingContractId: string, amount: number): Promise<void> {
    // Step 1: Withdraw the proposal
    await this.sdk.cantonExercise({
      contractId: proposalContractId,
      templateId: this.templateId('RWAMarketplace', 'ListingProposal'),
      choice: 'WithdrawListingProposal',
      argument: {},
    });

    // Step 2: Unlock tokens using the actual holding CID (passed from UI, always fresh)
    await this.sdk.cantonExercise({
      contractId: holdingContractId,
      templateId: this.templateId('RWAAsset', 'RWAHolding'),
      choice: 'UnlockTokens',
      argument: { unlockAmount: amount.toString() },
    });
  }

  async unlockTokens(holdingContractId: string, amount: number): Promise<void> {
    await this.sdk.cantonExercise({
      contractId: holdingContractId,
      templateId: this.templateId('RWAAsset', 'RWAHolding'),
      choice: 'UnlockTokens',
      argument: { unlockAmount: amount.toString() },
    });
  }

  async archiveStuckTrade(tradeContractId: string): Promise<void> {
    await this.sdk.cantonExercise({
      contractId: tradeContractId,
      templateId: this.templateId('RWAMarketplace', 'RWATrade'),
      choice: 'ArchiveStuckTrade',
      argument: {},
    });
  }

  // ============================================
  // Actions - Payment Deposit Requests
  // ============================================

  async requestPaymentDeposit(token: PaymentToken, amount: number): Promise<string> {
    const partyId = await this.sdk.getPartyId();
    if (!partyId) throw new Error('Not connected');

    const operatorParty = await this.getOperator();
    if (!operatorParty) throw new Error('Operator not found');

    const result = await this.sdk.cantonCreate({
      templateId: this.templateId('RWAMarketplace', 'PaymentDepositRequest'),
      payload: {
        operator: operatorParty,
        owner: partyId,
        token,
        amount: amount.toString(),
      },
    });

    return result.contractId;
  }

  async getPaymentDepositRequests(): Promise<PaymentDepositReq[]> {
    const contracts = await this.query<PaymentDepositRequestPayload>({
      templateId: this.templateId('RWAMarketplace', 'PaymentDepositRequest'),
    });

    return contracts.map(c => ({
      contractId: c.contractId,
      owner: c.payload.owner,
      token: c.payload.token.symbol,
      amount: parseFloat(c.payload.amount),
    }));
  }

  async approvePaymentDeposit(contractId: string): Promise<string> {
    const result = await this.sdk.cantonExercise({
      contractId,
      templateId: this.templateId('RWAMarketplace', 'PaymentDepositRequest'),
      choice: 'ApproveDeposit',
      argument: {},
    });

    const event = result.events?.find((e: any) => e.templateId?.includes('PaymentHolding'));
    return event?.contractId || '';
  }

  // ============================================
  // Actions - Get payment holding for a party
  // ============================================

  async getPaymentHoldingForParty(partyId: string, tokenSymbol: string = 'USDC'): Promise<PaymentBalance | null> {
    const balances = await this.getPaymentBalances(partyId);
    return balances.find(b => b.token === tokenSymbol) || null;
  }

  // ============================================
  // Actions - Purchase from Listing (InitiatePurchase + SettleTrade)
  // ============================================

  async purchaseFromListing(
    listingContractId: string,
    paymentContractId: string,
    amount: number
  ): Promise<{ tradeId: string }> {
    const buyerPartyId = await this.sdk.getPartyId();
    if (!buyerPartyId) throw new Error('Not connected');

    // Exercise InitiatePurchase on the listing (locks buyer payment, creates RWATrade)
    // Settlement is done separately by the operator via autoSettleTrades()
    const result = await this.sdk.cantonExercise({
      contractId: listingContractId,
      templateId: this.templateId('RWAMarketplace', 'RWAListing'),
      choice: 'InitiatePurchase',
      argument: {
        buyer: buyerPartyId,
        purchaseAmount: amount.toString(),
        buyerPaymentCid: paymentContractId,
        purchaseTime: new Date().toISOString(),
      },
    });

    const tradeEvent = result.events?.find((e: any) => e.templateId?.includes('RWATrade'));
    let tradeId = tradeEvent?.contractId || '';

    // If we couldn't get the trade ID from events, query for it
    if (!tradeId) {
      const trades = await this.getPendingTrades();
      const newTrade = trades.find(t => t.buyer === buyerPartyId);
      tradeId = newTrade?.contractId || '';
    }

    // Try to settle immediately (works if current user is also operator)
    if (tradeId) {
      try {
        await this.settleSingleTrade(tradeId);
        console.log('[RWA] Trade settled immediately after purchase');
      } catch (err) {
        console.log('[RWA] Immediate settle failed (operator will auto-settle):', err instanceof Error ? err.message : err);
      }
    }

    return { tradeId };
  }

  /**
   * Auto-settle all pending trades. Must be called by the operator.
   * Finds required payment/holding CIDs and settles each trade atomically.
   */
  private failedTrades = new Set<string>();
  private settling = false;

  async autoSettleTrades(): Promise<number> {
    if (this.settling) {
      console.log('[RWA] autoSettleTrades: already settling, skipping');
      return 0;
    }
    this.settling = true;
    try {
      return await this._doAutoSettleTrades();
    } finally {
      this.settling = false;
    }
  }

  private async _doAutoSettleTrades(): Promise<number> {
    const trades = await this.getPendingTrades();
    if (trades.length === 0) return 0;

    const platform = await this.getPlatform();
    const feeCollector = platform?.payload.config.feeCollector || '';
    const feePercent = platform?.payload.config.platformFeePercent || '0';

    let settled = 0;
    for (const trade of trades) {
      if (this.failedTrades.has(trade.contractId)) continue;
      try {
        await this.settleSingleTrade(trade.contractId, feePercent, feeCollector);
        settled++;
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        if (errMsg.includes('CONTRACT_NOT_FOUND')) {
          console.warn(`[RWA] Trade ${trade.contractId} has stale CIDs, attempting cleanup...`);
          this.failedTrades.add(trade.contractId);

          // Note: UnlockTokens (controller=owner) and UnlockPayment (controller=owner)
          // cannot be exercised by the operator standalone. Seller/buyer must unlock
          // their own funds manually after the stuck trade is archived.
          console.warn(`[RWA] Trade ${trade.contractId} will be archived. Seller/buyer must manually unlock any stuck funds.`);

          // Archive the stuck trade
          try {
            await this.sdk.cantonExercise({
              contractId: trade.contractId,
              templateId: this.templateId('RWAMarketplace', 'RWATrade'),
              choice: 'ArchiveStuckTrade',
              argument: {},
            });
            console.log(`[RWA] Archived stuck trade ${trade.contractId}`);
          } catch (_) { /* best effort */ }
        } else {
          console.warn(`Failed to settle trade ${trade.contractId}:`, err);
        }
      }
    }
    return settled;
  }

  async settleSingleTrade(tradeContractId: string, feePercent?: string, feeCollector?: string): Promise<void> {
    if (!feePercent || !feeCollector) {
      const platform = await this.getPlatform();
      feeCollector = feeCollector || platform?.payload.config.feeCollector || '';
      feePercent = feePercent || platform?.payload.config.platformFeePercent || '0';
    }

    const tradeContracts = await this.query<RWATradePayload>({
      templateId: this.templateId('RWAMarketplace', 'RWATrade'),
    });
    const fullTrade = tradeContracts.find(t => t.contractId === tradeContractId);
    if (!fullTrade) throw new Error('Trade not found');

    const sellerPayments = await this.getPaymentBalances(fullTrade.payload.seller);
    const sellerPayment = sellerPayments.find(b => b.token === fullTrade.payload.paymentToken.symbol);

    const buyerHoldings = await this.getHoldings(fullTrade.payload.buyer);
    const existingBuyerHolding = buyerHoldings.find(h => h.assetId === fullTrade.payload.assetId);

    await this.sdk.cantonExercise({
      contractId: tradeContractId,
      templateId: this.templateId('RWAMarketplace', 'RWATrade'),
      choice: 'SettleTrade',
      argument: {
        feePercent,
        feeCollector: feeCollector || fullTrade.payload.operator,
        sellerPaymentCid: sellerPayment ? sellerPayment.contractId : null,
        buyerHoldingCid: existingBuyerHolding
          ? existingBuyerHolding.contractId
          : null,
      },
    });
  }

  // ============================================
  // Role Detection
  // ============================================

  async detectRoles(partyId: string, operatorParty: string): Promise<UserRoles> {
    const roles = new Set<UserRole>();
    let issuerLicense: UserRoles['issuerLicense'] = null;
    let verifierContract: UserRoles['verifierContract'] = null;

    // Everyone is at least an investor
    roles.add('investor');

    // Check if operator
    if (partyId === operatorParty) {
      roles.add('operator');
    }

    // Check if licensed issuer
    try {
      const licenses = await this.query<IssuerLicensePayload>({
        templateId: this.templateId('RWAOperator', 'RWAIssuerLicense'),
      });

      const license = licenses.find(l => l.payload.issuer === partyId);
      if (license) {
        roles.add('issuer');
        issuerLicense = {
          contractId: license.contractId,
          allowedCategories: license.payload.allowedCategories.map(c => c.tag),
        };
      }
    } catch (err) {
      console.warn('Failed to query issuer licenses:', err);
    }

    // Check if verifier
    try {
      const verifiers = await this.query<VerifierPayload>({
        templateId: this.templateId('RWAOperator', 'RWAVerifier'),
      });

      const verifier = verifiers.find(v => v.payload.verifier === partyId);
      if (verifier) {
        roles.add('verifier');
        verifierContract = {
          contractId: verifier.contractId,
        };
      }
    } catch (err) {
      console.warn('Failed to query verifiers:', err);
    }

    return {
      roles,
      isOperator: roles.has('operator'),
      isIssuer: roles.has('issuer'),
      isVerifier: roles.has('verifier'),
      isInvestor: roles.has('investor'),
      issuerLicense,
      verifierContract,
    };
  }

  // ============================================
  // Operator Actions
  // ============================================

  async getPlatform(): Promise<{ contractId: string; payload: PlatformPayload } | null> {
    const contracts = await this.query<PlatformPayload>({
      templateId: this.templateId('RWAOperator', 'RWAPlatform'),
      readAs: this.publicParty ? [this.publicParty] : undefined,
    });
    return contracts[0] || null;
  }

  async getOperator(): Promise<string | null> {
    const platform = await this.getPlatform();
    return platform?.payload.operator || this.operatorPartyOverride || null;
  }

  /**
   * Initialize the RWA Platform - only call once!
   * Creates the singleton RWAPlatform contract with the caller as operator.
   */
  async initializePlatform(config: {
    platformName: string;
    platformFeePercent: string;
    feeCollector: string;
    operator: string;
    publicParty: string;
  }): Promise<string> {
    // Check if platform already exists
    const existing = await this.getPlatform();
    if (existing) {
      throw new Error('Platform already initialized');
    }

    const result = await this.sdk.cantonCreate({
      templateId: this.templateId('RWAOperator', 'RWAPlatform'),
      payload: {
        operator: config.operator,
        public: config.publicParty,
        config: {
          platformName: config.platformName,
          platformFeePercent: config.platformFeePercent,
          feeCollector: config.feeCollector,
          allowedPaymentTokens: [],
          defaultCompliance: {
            requireKYC: true,
            requireAccreditedInvestor: false,
            jurisdictionWhitelist: [],
            jurisdictionBlacklist: [],
            maxHolders: null,
            transferRestrictionPeriod: null,
          },
          paused: false,
        },
        issuers: [],
        verifiers: [],
        investors: [],
      },
    });

    return result.contractId;
  }

  /**
   * Self-register as investor - no approval needed
   * Creates InvestorSelfProfile directly with investor as signatory
   * Also grants readAs rights for the public party so investor can see public contracts
   */
  async selfRegisterAsInvestor(
    operatorParty: string,
    jurisdiction: string = 'US'
  ): Promise<string> {
    const partyId = await this.sdk.getPartyId();
    if (!partyId) {
      throw new Error('Not connected');
    }

    const result = await this.sdk.cantonCreate({
      templateId: this.templateId('RWAOperator', 'InvestorSelfProfile'),
      payload: {
        investor: partyId,
        operatorParty: operatorParty,
        jurisdiction,
        status: 'Unverified',
      },
    });

    // Grant readAs rights for the public party so investor can see public contracts
    if (this.publicParty) {
      try {
        await this.sdk.grantUserRights({
          userId: partyId,
          rights: [{ type: 'readAs', party: this.publicParty }],
        });
      } catch (err) {
        // Log but don't fail - the profile was created successfully
        console.warn('Failed to grant public party readAs rights:', err);
      }
    }

    return result.contractId;
  }

  /**
   * Get self-registered investor profile
   */
  async getMySelfProfile(): Promise<InvestorProfile | null> {
    const partyId = await this.sdk.getPartyId();
    if (!partyId) return null;

    const contracts = await this.query<{
      investor: string;
      operatorParty: string;
      jurisdiction: string;
      status: { tag: InvestorStatus };
    }>({
      templateId: this.templateId('RWAOperator', 'InvestorSelfProfile'),
    });

    const profile = contracts.find(c => c.payload.investor === partyId);
    if (!profile) return null;

    return {
      contractId: profile.contractId,
      investor: profile.payload.investor,
      status: profile.payload.status.tag,
      jurisdiction: profile.payload.jurisdiction,
      verifier: profile.payload.operatorParty, // Use operator as placeholder
    };
  }

  async getIssuerLicenses(): Promise<Array<{
    contractId: string;
    issuer: string;
    issuerName: string;
    allowedCategories: string[];
  }>> {
    const contracts = await this.query<IssuerLicensePayload>({
      templateId: this.templateId('RWAOperator', 'RWAIssuerLicense'),
    });

    return contracts.map(c => ({
      contractId: c.contractId,
      issuer: c.payload.issuer,
      issuerName: c.payload.issuerName,
      allowedCategories: c.payload.allowedCategories.map(cat => cat.tag),
    }));
  }

  async getIssuerProposals(): Promise<Array<{
    contractId: string;
    issuer: string;
    issuerName: string;
    allowedCategories: string[];
  }>> {
    const contracts = await this.query<IssuerProposalPayload>({
      templateId: this.templateId('RWAOperator', 'IssuerProposal'),
    });

    return contracts.map(c => ({
      contractId: c.contractId,
      issuer: c.payload.issuer,
      issuerName: c.payload.issuerName,
      allowedCategories: c.payload.allowedCategories.map(cat => cat.tag),
    }));
  }

  async appointIssuer(
    platformContractId: string,
    issuerParty: string,
    issuerName: string,
    allowedCategories: AssetCategory[]
  ): Promise<string> {
    const result = await this.sdk.cantonExercise({
      contractId: platformContractId,
      templateId: this.templateId('RWAOperator', 'RWAPlatform'),
      choice: 'AppointIssuer',
      argument: {
        newIssuer: issuerParty,
        issuerName,
        allowedCategories: allowedCategories.map(c => ({ tag: c, value: {} })),
      },
    });

    const event = result.events?.find(e => e.templateId?.includes('IssuerProposal'));
    return event?.contractId || '';
  }

  async revokeIssuer(
    platformContractId: string,
    issuerLicenseContractId: string
  ): Promise<void> {
    await this.sdk.cantonExercise({
      contractId: platformContractId,
      templateId: this.templateId('RWAOperator', 'RWAPlatform'),
      choice: 'RevokeIssuer',
      argument: {
        issuerLicenseCid: issuerLicenseContractId,
      },
    });
  }

  async getAssetProposals(): Promise<Array<{
    contractId: string;
    issuer: string;
    assetId: string;
    name: string;
    category: string;
    assetType: string;
    totalSupply: number;
  }>> {
    const contracts = await this.query<AssetProposalPayload>({
      templateId: this.templateId('RWAAsset', 'AssetIssuanceProposal'),
    });

    return contracts.map(c => ({
      contractId: c.contractId,
      issuer: c.payload.issuer,
      assetId: c.payload.assetId,
      name: c.payload.name,
      category: c.payload.category.tag,
      assetType: c.payload.assetType.tag,
      totalSupply: parseFloat(c.payload.totalSupply),
    }));
  }

  async approveAssetProposal(proposalContractId: string): Promise<string> {
    const result = await this.sdk.cantonExercise({
      contractId: proposalContractId,
      templateId: this.templateId('RWAAsset', 'AssetIssuanceProposal'),
      choice: 'ApproveIssuance',
      argument: {
        approvalTime: new Date().toISOString(),
      },
    });

    const event = result.events?.find(e => e.templateId?.includes('RWAAsset'));
    return event?.contractId || '';
  }

  async rejectAssetProposal(proposalContractId: string, reason: string): Promise<void> {
    await this.sdk.cantonExercise({
      contractId: proposalContractId,
      templateId: this.templateId('RWAAsset', 'AssetIssuanceProposal'),
      choice: 'RejectIssuance',
      argument: { reason },
    });
  }

  async settleTrade(
    tradeContractId: string,
    sellerPaymentContractId: string
  ): Promise<string> {
    // Get the trade to find the buyer
    const tradeContracts = await this.query<RWATradePayload>({
      templateId: this.templateId('RWAMarketplace', 'RWATrade'),
    });
    const trade = tradeContracts.find(t => t.contractId === tradeContractId);

    const platform = await this.getPlatform();
    const feeCollector = platform?.payload.config.feeCollector || trade?.payload.operator || '';
    const feePercent = platform?.payload.config.platformFeePercent || '0';

    // Find buyer's existing holding for this asset (if any)
    let buyerHoldingCid: string | null = null;
    if (trade) {
      const buyerHoldings = await this.getHoldings(trade.payload.buyer);
      const existing = buyerHoldings.find(h => h.assetId === trade.payload.assetId);
      if (existing) {
        buyerHoldingCid = existing.contractId;
      }
    }

    const result = await this.sdk.cantonExercise({
      contractId: tradeContractId,
      templateId: this.templateId('RWAMarketplace', 'RWATrade'),
      choice: 'SettleTrade',
      argument: {
        feePercent,
        feeCollector,
        sellerPaymentCid: sellerPaymentContractId,
        buyerHoldingCid,
      },
    });

    const event = result.events?.find(e => e.templateId?.includes('TradeSettlement'));
    return event?.contractId || '';
  }

  // ============================================
  // Issuer Actions
  // ============================================

  async proposeAsset(
    operatorParty: string,
    publicParty: string,
    assetDetails: {
      assetId: string;
      name: string;
      symbol: string;
      category: AssetCategory;
      assetType: AssetType;
      totalSupply: number;
      compliance: Partial<ComplianceConfig>;
      underlying: Partial<UnderlyingAsset>;
    }
  ): Promise<string> {
    // DEBUG: Log what values are being used for asset creation
    console.log('[RWA] proposeAsset called with:', { operatorParty, publicParty });

    const partyId = await this.sdk.getPartyId();
    if (!partyId) {
      throw new Error('Not connected');
    }

    const compliance: ComplianceConfig = {
      requireAccreditedInvestor: assetDetails.compliance.requireAccreditedInvestor ?? false,
      requireKYC: assetDetails.compliance.requireKYC ?? true,
      maxHolders: assetDetails.compliance.maxHolders ?? null,
      maxHoldingPercentage: assetDetails.compliance.maxHoldingPercentage ?? null,
      minInvestmentAmount: assetDetails.compliance.minInvestmentAmount ?? null,
      transferRestrictionDays: assetDetails.compliance.transferRestrictionDays ?? null,
      jurisdictionWhitelist: assetDetails.compliance.jurisdictionWhitelist ?? [],
      jurisdictionBlacklist: assetDetails.compliance.jurisdictionBlacklist ?? [],
    };

    const underlying: UnderlyingAsset = {
      description: assetDetails.underlying.description ?? '',
      legalEntity: assetDetails.underlying.legalEntity ?? null,
      custodian: assetDetails.underlying.custodian ?? null,
      valuationDate: assetDetails.underlying.valuationDate ?? null,
      valuationAmount: assetDetails.underlying.valuationAmount ?? null,
      valuationCurrency: assetDetails.underlying.valuationCurrency ?? 'USD',
      documents: assetDetails.underlying.documents ?? [],
      physicalLocation: assetDetails.underlying.physicalLocation ?? null,
    };

    // Create AssetIssuanceProposal directly - issuer is signatory, operator is observer
    const result = await this.sdk.cantonCreate({
      templateId: this.templateId('RWAAsset', 'AssetIssuanceProposal'),
      payload: {
        operator: operatorParty,
        public: publicParty,
        issuer: partyId,
        assetId: assetDetails.assetId,
        name: assetDetails.name,
        symbol: assetDetails.symbol,
        description: assetDetails.underlying.description ?? '',
        category: { tag: assetDetails.category, value: {} },
        assetType: assetDetails.assetType,
        totalSupply: assetDetails.totalSupply.toString(),
        decimals: 0,
        underlying,
        compliance,
        imageUrl: null,
        metadata: [],
      },
    });

    return result.contractId;
  }

  async mintTokens(
    assetContractId: string,
    recipient: string,
    amount: number
  ): Promise<string> {
    const result = await this.sdk.cantonExercise({
      contractId: assetContractId,
      templateId: this.templateId('RWAAsset', 'RWAAsset'),
      choice: 'MintTokens',
      argument: {
        recipient,
        amount: amount.toString(),
      },
    });

    const event = result.events?.find(e => e.templateId?.includes('RWAHolding'));
    return event?.contractId || '';
  }

  async mintNFT(
    assetContractId: string,
    recipient: string
  ): Promise<string> {
    const result = await this.sdk.cantonExercise({
      contractId: assetContractId,
      templateId: this.templateId('RWAAsset', 'RWAAsset'),
      choice: 'MintNFT',
      argument: {
        recipient,
      },
    });

    const event = result.events?.find(e => e.templateId?.includes('RWAHolding'));
    return event?.contractId || '';
  }

  async getMyIssuedAssets(issuerParty: string): Promise<RWAAsset[]> {
    const assets = await this.getAssets();
    return assets.filter(a => a.issuer === issuerParty);
  }

  // ============================================
  // Verifier Actions
  // ============================================

  async verifyInvestor(
    verifierContractId: string,
    investorParty: string,
    status: 'Unverified' | 'KYCVerified' | 'AccreditedVerified' | 'InstitutionalVerified',
    jurisdiction: string,
    expiryDate?: string
  ): Promise<string> {
    const result = await this.sdk.cantonExercise({
      contractId: verifierContractId,
      templateId: this.templateId('RWAOperator', 'RWAVerifier'),
      choice: 'VerifyInvestor',
      argument: {
        investor: investorParty,
        status: { tag: status, value: {} },
        jurisdiction,
        expiryDate: expiryDate || null,
      },
    });

    const event = result.events?.find(e => e.templateId?.includes('RWAInvestorProfile'));
    return event?.contractId || '';
  }

  async getInvestorProfiles(): Promise<InvestorProfile[]> {
    const contracts = await this.query<InvestorProfilePayload>({
      templateId: this.templateId('RWAOperator', 'RWAInvestorProfile'),
    });

    return contracts.map(c => ({
      contractId: c.contractId,
      investor: c.payload.investor,
      status: c.payload.status.tag,
      jurisdiction: c.payload.jurisdiction,
      verifier: c.payload.verifier,
    }));
  }
}

// ============================================
// Singleton Instance
// ============================================

let rwaServiceInstance: RWAService | null = null;

export function getRWAService(packageId: string): RWAService {
  if (!rwaServiceInstance) {
    rwaServiceInstance = new RWAService(packageId);
  }
  return rwaServiceInstance;
}

export function initRWAService(packageId: string, sdk?: StratosSDK): RWAService {
  rwaServiceInstance = new RWAService(packageId, sdk);
  return rwaServiceInstance;
}
